import React, { FunctionComponent, useRef, useState } from "react";
import ImageSlideshowBlockProps from "./ImageSlideshowProps.interface";
import styles from "./ImageSlideshow.module.scss";
import combineClasses from "helpers/combine-classes";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import getImageCrop from "app/media/helpers/getImageCrop";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import useHover from "./hooks/useHover";
import useInterval from "./hooks/useInterval";
import BackgroundMedia from "components/BackgroundMedia/BackgroundMedia";
import ImageViewer from "components/ImageViewer/ImageViewer";
import IconViewer from "components/IconViewer/IconViewer";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import setStyles from "./helpers/setStyles";
import useAnchor from "blocks/hooks/useAnchor";

const ImageSlideshow: FunctionComponent<ImageSlideshowBlockProps> = (props) => {
    const [currentSlide, setCurrentSlide] = useState(0);

    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const identifier = useAnchor(props.settings.anchorMenuLabel);
    const { splitClasses, splitStyles } = useSplitBackground(props);
    const { containerStyles, containerClasses } = setStyles(props.settings, styles);

    const imageCrop = getImageCrop(props.content.image?.umbracoFile.crops, props.settings.imageCrop, "Fullscreen (4:3)");
    const imageAspectRatio = getImageAspectRatio(imageCrop, props.content.image?.umbracoWidth, props.content.image?.umbracoHeight);
    const slideshowSizeClass = props.settings.slideshowSize ? styles[props.settings.slideshowSize] : undefined;
    const delay = props.settings.slidesDelay ? props.settings.slidesDelay * 1000 : 3000;

    const wrapperStylesElementRef = useRef<any>(null);
    const isHover = useHover(wrapperStylesElementRef);

    useInterval(() => {
        handleClickRight();
    }, !isHover ? delay : null);

    const handleClickLeft = () => {
        if (currentSlide === 0 && props.content.slides) {
            setCurrentSlide(props.content.slides.length - 1);
        }
        else {
            setCurrentSlide(currentSlide - 1);
        }
    };

    const handleClickRight = () => {
        if (props.content.slides && currentSlide === props.content.slides.length - 1) {
            setCurrentSlide(0);
        }
        else {
            setCurrentSlide(currentSlide + 1);
        }
    };

    const handlePaginationClick = (index: number) => {
        setCurrentSlide(index);
    };

    const controlsOutsideClass = props.settings.controlsOutside === true ? styles.controls_outside : undefined;

    return (
        <section className={combineClasses(styles.wrapper, slideshowSizeClass, controlsOutsideClass, containerClasses, ...wrapperClasses)} style={{ ...wrapperStyles, ...splitStyles, ...containerStyles }} id={identifier}>
            <div className={combineClasses(styles.container, ...splitClasses, slideshowSizeClass)}>
                <div className={styles.slider_wrapper}>
                    {props.content.slides && props.content.slides.length > 0 ? props.content.slides.map((slide, index) => {
                        const activeSlideClass = currentSlide === index ? styles.is_active : styles.is_inactive;

                        return (
                            <div key={slide.contentUdi} className={combineClasses(styles.image_wrapper, activeSlideClass)}>
                                {slide.content.slideImage ? (
                                    <ImageViewer src={slide.content.slideImage.umbracoFile.src} extensionType={slide.content.slideImage.umbracoExtension} focalPoint={slide.content.slideImage.umbracoFile.focalPoint} alt={slide.content.imageAlternative ? slide.content.imageAlternative : ""} aspectRatioX={imageAspectRatio.aspectRatioX} aspectRatioY={imageAspectRatio.aspectRatioY} />
                                ) : null}
                            </div>
                        );
                    }) : null}
                </div>

                <div className={styles.navigation}>
                    {/* Controls */}
                    <div className={styles.controls}>
                        <button className={styles.control_button} onClick={() => handleClickLeft()}>
                            <IconViewer className={styles.control_icon} name="chevron-circle-left" />
                        </button>
                        <button className={styles.control_button} onClick={() => handleClickRight()}>
                            <IconViewer className={styles.control_icon} name="chevron-circle-right" />
                        </button>
                    </div>

                    {/* Pagination */}
                    <div className={styles.pagination}>
                        {props.content.slides && props.content.slides.map((_, index) => {
                            const currentImageClass = currentSlide === index ? styles.is_active : styles.is_inactive;
                            return (
                                <button key={index} className={styles.pagination_item}>
                                    <span className={combineClasses(styles.pagination_dot, currentImageClass)} onClick={() => handlePaginationClick(index)}></span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {props.settings.backgroundMedia?.[0] && props.settings.backgroundMedia.length > 0 ? (
                <BackgroundMedia key={props.contentUdi} {...props.settings.backgroundMedia[0]} />
            ) : null}
        </section>
    );
};

export default ImageSlideshow;
